import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";
import {
  ContactFileModel,
  ContactPreviewFileModel,
  FileModel,
} from "../factories/contacts/models/contact-file.model";
// Circular with DocumentsSlice on purpose: both only use each other inside
// mutation callbacks, never while the modules load.
import { documentsSlice } from "./DocumentsSlice";

/**
 * Files added or deleted through these older calls (work Notes chat
 * paperclip, profile Files cards, signatures) also refresh the Documents
 * lists, which the Files tabs and their tab counts read (Roadmap 1842).
 */
const refreshDocuments = async (
  _arg: unknown,
  {
    dispatch,
    queryFulfilled,
  }: { dispatch: (action: unknown) => void; queryFulfilled: Promise<unknown> },
) => {
  try {
    await queryFulfilled;
  } catch {
    return;
  }
  dispatch(documentsSlice.util.invalidateTags(["ClientFiles", "WorkFiles"]));
};

export const filesSlice = createApi({
  reducerPath: "filesSlice",
  tagTypes: ["ContactFiles", "WorkFile"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      const authHeader = getAuthHeader();
      headers.set("Authorization", authHeader.Authorization);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getContactFiles: build.query<FileModel, string>({
      query: (contactId) => ({
        url: "api/contact-documents/get-by-contactId/" + contactId,
      }),
      providesTags: () => ["ContactFiles"],
    }),
    getOrgContactFiles: build.query<FileModel, string>({
      query: (organizationId) => ({
        url: "api/contact-documents/get-by-busnessContactId/" + organizationId,
      }),
      providesTags: () => ["ContactFiles"],
    }),
    getPreviewContactFile: build.query<ContactPreviewFileModel, string>({
      query: (id) => ({
        url: "api/contact-documents/preview-document/" + id,
      }),
    }),
    addContactFile: build.mutation<unknown, FormData>({
      query: (body) => ({
        url: "api/contact-documents/add-document",
        method: "POST",
        body,
      }),
      onQueryStarted: refreshDocuments,
      invalidatesTags: () => ["ContactFiles"],
    }),
    deleteContactFile: build.mutation<unknown, string>({
      query: (id) => ({
        url: "api/contact-documents",
        method: "DELETE",
        params: { docId: id },
      }),
      onQueryStarted: refreshDocuments,
      invalidatesTags: () => ["ContactFiles"],
    }),

    /** Work files*/

    getWorkFiles: build.query<ContactFileModel[], string>({
      query: (workId) => ({
        url: "api/documents/get-by-workId/" + workId,
      }),
      providesTags: () => ["WorkFile"],
    }),
    getPreviewWorkFile: build.query<ContactPreviewFileModel, string>({
      query: (id) => ({
        url: "api/documents/preview-document/" + id,
      }),
    }),
    addWorkFile: build.mutation<unknown, FormData>({
      query: (body) => ({
        url: "api/documents/add-document",
        method: "POST",
        body,
      }),
      onQueryStarted: refreshDocuments,
      invalidatesTags: () => ["WorkFile", "ContactFiles"],
    }),
    deleteWorkFile: build.mutation<unknown, string>({
      query: (id) => ({
        url: "api/documents",
        method: "DELETE",
        params: { docId: id },
      }),
      onQueryStarted: refreshDocuments,
      invalidatesTags: () => ["WorkFile", "ContactFiles"],
    }),
  }),
});
export const {
  useGetContactFilesQuery,
  useGetOrgContactFilesQuery,
  useLazyGetPreviewContactFileQuery,
  useAddContactFileMutation,
  useDeleteContactFileMutation,

  /** Work files*/

  useGetWorkFilesQuery,
  useLazyGetWorkFilesQuery,
  useLazyGetPreviewWorkFileQuery,
  useAddWorkFileMutation,
  useDeleteWorkFileMutation,
} = filesSlice;
